import React from 'react';
import {
    Typography,
    IconButton,
    Icon,
    Tooltip
} from '@material-ui/core';

/**
 * VolumeChips - A reusable component for displaying volume chips with action icons
 * across different cloud providers (AWS, Azure, GCP, OCI, MAAS)
 * 
 * @param {Object} props
 * @param {Array} props.volumes - Array of volume objects
 * @param {Array} props.availableStorageConfigs - Array of available storage configurations
 * @param {Function} props.onEdit - Function to handle edit action
 * @param {Function} props.onDelete - Function to handle delete action
 * @param {number} props.nodeGroupIndex - The index of the node group
 * @param {Object} props.style - Additional styling for the container
 * @param {boolean} props.showActions - Whether to show action buttons (default: true)
 */
const VolumeChips = ({
    volumes = [],
    availableStorageConfigs = [],
    onEdit,
    onDelete,
    nodeGroupIndex,
    style = {},
    showActions = true
}) => {
    if (!volumes || volumes.length === 0) {
        return (
            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                No volumes attached
            </Typography>
        );
    }

    const defaultContainerStyle = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        ...style
    };

    return (
        <div style={defaultContainerStyle}>
            {volumes.map((volume, volIndex) => {
                // Find storage config to get name
                const storageConfig = availableStorageConfigs.find(config => config.id === volume.storageConfigId);
                const storageConfigName = storageConfig?.name || storageConfig?.providerName || 'Unknown Storage';
                
                return (
                    <VolumeChip
                        key={volume.id}
                        volume={volume}
                        storageConfig={storageConfig}
                        storageConfigName={storageConfigName}
                        onEdit={showActions ? () => onEdit?.(volume, storageConfig, nodeGroupIndex) : undefined}
                        onDelete={showActions ? () => onDelete?.(nodeGroupIndex, volume.id) : undefined}
                        showActions={showActions}
                    />
                );
            })}
        </div>
    );
};

/**
 * VolumeChip - Individual volume chip component with action icons
 */
const VolumeChip = ({
    volume,
    storageConfig,
    storageConfigName,
    onEdit,
    onDelete,
    showActions = true
}) => {
    const chipStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: '#E3F2FD',
        borderRadius: '16px',
        padding: '4px 10px',
        height: '26px',
        marginRight: '8px',
        marginBottom: '4px',
        border: '1px solid #BBDEFB'
    };

    const actionsStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '2px'
    };

    const iconButtonStyle = {
        padding: '1px',
        width: '16px',
        height: '16px'
    };

    const iconStyle = {
        fontSize: '11px'
    };

    return (
        <div style={chipStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', marginRight: showActions ? '8px' : '0' }}>
                <Typography variant="body2" style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#1565C0',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {`${storageConfigName} (${volume.size}GB)`}
                </Typography>
                {volume.performanceOverrides?.readIops && (
                    <Typography variant="caption" style={{
                        fontSize: '10px',
                        color: '#757575',
                        lineHeight: 1
                    }}>
                        IOPS: {volume.performanceOverrides.readIops}R/{volume.performanceOverrides.writeIops || volume.performanceOverrides.readIops}W
                    </Typography>
                )}
            </div>
            
            {showActions && (
                <div style={actionsStyle}>
                    {/* Edit Icon */}
                    <Tooltip title="Edit Details" placement="top">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onEdit) onEdit();
                            }}
                            style={{
                                ...iconButtonStyle,
                                color: '#1976D2',
                                backgroundColor: 'rgba(25, 118, 210, 0.1)'
                            }}
                        >
                            <Icon style={iconStyle}>edit</Icon>
                        </IconButton>
                    </Tooltip>
                    
                    {/* Delete Icon */}
                    <Tooltip title="Remove Volume" placement="top">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onDelete) onDelete();
                            }}
                            style={{
                                ...iconButtonStyle,
                                color: '#D32F2F',
                                backgroundColor: 'rgba(211, 47, 47, 0.1)'
                            }}
                        >
                            <Icon style={iconStyle}>delete</Icon>
                        </IconButton>
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

export default VolumeChips;
